import { useRef, useMemo, type KeyboardEvent } from "react";
import { ProjectTabLink } from "@/components/project/ProjectTabLink";
import { moveHighlight } from "@/lib/list-navigation";
import { cn } from "@/lib/utils";
import { useProjectDetailContext } from "@/contexts/ProjectDetailContext";
import { usePermissions } from "@/hooks/use-permissions";
import { useFeatureFlag } from "@/hooks/use-feature-flag";

/**
 * Tab navigation for project detail pages using NavLink + nested routes.
 * URL-based active state; browser back/forward works natively.
 *
 * Rolle-gating:
 * - Timetildeling: canSeeBudget (admin, produksjonsleder, eier)
 * - Godkjenne ferdige oppgaver: kun produksjonsleder (eller admin)
 */
export function ProjectDetailTabNav() {
  const tabsRef = useRef<HTMLDivElement>(null);
  const { project, budgetLines, vouchers, canSeeBudget, isProductionLead, isAdmin } = useProjectDetailContext();
  const { isSuperadmin } = usePermissions();
  const equipmentEnabled = useFeatureFlag("feature_equipment");
  const locationsEnabled = useFeatureFlag("feature_locations");

  const canApprove = isProductionLead || isAdmin;

  const completedTasksCount = useMemo(
    () =>
      budgetLines
        .flatMap((bl) => bl.assignments ?? [])
        .filter((a: any) => a.task_status === "completed").length,
    [budgetLines]
  );

  const pendingVouchersCount = useMemo(
    () => vouchers.filter((v) => v.status === "pending").length,
    [vouchers]
  );

  // Budget allocation indicator dot
  const internalBudgetLines = useMemo(
    () => budgetLines.filter((l) => l.cost_type === "internal"),
    [budgetLines]
  );
  const totalBudgetInternalHours = useMemo(
    () => internalBudgetLines.reduce((s, l) => s + (l.quantity_hours ?? 0), 0),
    [internalBudgetLines]
  );
  const totalBudgetAssignedHours = useMemo(
    () =>
      internalBudgetLines.reduce(
        (s, l) => s + l.assignments.reduce((as, a) => as + a.hours, 0),
        0
      ),
    [internalBudgetLines]
  );

  const isInternal = project.is_internal;
  const isHourly = project.project_type === 'hourly';
  const hideAllocation = isInternal || isHourly;
  const base = `/prosjekter/${project.id}`;

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      "inline-flex items-center whitespace-nowrap px-3 py-3 xl:py-2 min-h-[44px] xl:min-h-0 text-sm font-medium border-b-2 transition-colors",
      isActive
        ? "border-primary text-primary"
        : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
    );

  // Piltaster mellom fanene (VIB-2541). Alle fanene er fortsatt i Tab-rekkefølgen,
  // så en ukjent underrute (uten aktiv fane) aldri stenger tastaturet ute.
  const handleTabKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const key =
      e.key === "ArrowRight" ? "ArrowDown" : e.key === "ArrowLeft" ? "ArrowUp" : e.key === "Home" || e.key === "End" ? e.key : null;
    if (!key) return;
    const tabs = Array.from(tabsRef.current?.querySelectorAll<HTMLAnchorElement>('[role="tab"]') ?? []);
    const current = tabs.indexOf(document.activeElement as HTMLAnchorElement);
    if (current < 0) return;
    e.preventDefault();
    const next = tabs[moveHighlight(current, key, tabs.length)];
    next?.focus();
    next?.click();
  };

  return (
    <div
      ref={tabsRef}
      className="overflow-x-auto mb-6 scroll-mt-4 -mx-4 sm:mx-0 px-4 sm:px-0 border-b border-border/50"
    >
      <div className="inline-flex min-w-max gap-1" role="tablist" aria-label="Prosjektfaner" onKeyDown={handleTabKeyDown}>
        <ProjectTabLink to={base} end className={linkClass}>
          {hideAllocation ? "Timer" : "Mine oppgaver"}
        </ProjectTabLink>

        {canSeeBudget && !hideAllocation && (
          <ProjectTabLink to={`${base}/budsjett`} className={linkClass}>
            Timetildeling
            {totalBudgetInternalHours > 0 && totalBudgetAssignedHours < totalBudgetInternalHours && (
              <span
                className={cn(
                  "ml-1.5 w-2 h-2 rounded-full",
                  totalBudgetAssignedHours > 0 ? "bg-amber-500" : "bg-red-500"
                )}
              />
            )}
            {totalBudgetInternalHours > 0 && totalBudgetAssignedHours >= totalBudgetInternalHours && (
              <span className="ml-1.5 w-2 h-2 rounded-full bg-green-500" />
            )}
          </ProjectTabLink>
        )}

        {isSuperadmin && (
          <ProjectTabLink to={`${base}/admin-timer`} className={linkClass}>
            Admin timer
          </ProjectTabLink>
        )}

        {canApprove && !hideAllocation && (
          <ProjectTabLink to={`${base}/godkjenne`} className={linkClass}>
            Godkjenne ferdige oppgaver
            {completedTasksCount > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-semibold bg-yellow-100 text-yellow-700 rounded-full">
                {completedTasksCount}
              </span>
            )}
          </ProjectTabLink>
        )}

        {!isInternal && (
          <ProjectTabLink to={`${base}/okonomi`} className={linkClass}>
            Økonomi
            {pendingVouchersCount > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-semibold bg-orange-100 text-orange-700 rounded-full">
                {pendingVouchersCount}
              </span>
            )}
          </ProjectTabLink>
        )}

        <ProjectTabLink to={`${base}/dokumenter`} className={linkClass}>
          Dokumenter
        </ProjectTabLink>

        {equipmentEnabled && (
          <ProjectTabLink to={`${base}/utstyr`} className={linkClass}>
            Utstyr
          </ProjectTabLink>
        )}

        {locationsEnabled && (
          <ProjectTabLink to={`${base}/lokasjoner`} className={linkClass}>
            Lokasjoner
          </ProjectTabLink>
        )}

        <ProjectTabLink to={`${base}/logg`} className={linkClass}>
          Logg
        </ProjectTabLink>
      </div>
    </div>
  );
}
